import { MdPerson, MdGroup } from 'react-icons/md'
import { HiSparkles } from 'react-icons/hi2'
import { InternalConversation } from '@/lib/api/internal-chat/internal-chat-api'

interface ConversationItemProps {
    conversation: InternalConversation
    isActive: boolean
    currentUserId: number
    onClick: () => void
}

export default function ConversationItem({
    conversation,
    isActive,
    currentUserId,
    onClick,
}: ConversationItemProps) {
    const getTitle = () => {
        if (conversation.type === 'AI_CHAT') {
            return 'Assistente IA'
        }

        if (conversation.type === 'BROADCAST_ALL') {
            return conversation.title || 'Chat Geral'
        }

        if (conversation.type === 'BROADCAST_TYPE') {
            return (
                conversation.title ||
                `Para ${getUserTypeLabel(conversation.targetUserType)}`
            )
        }

        // PRIVATE - show other participant's name
        if (conversation.participants) {
            const otherParticipant = conversation.participants.find(
                p => p.id !== currentUserId
            )
            return otherParticipant?.name || 'Conversa'
        }

        return 'Conversa'
    }

    const getUserTypeLabel = (type?: string) => {
        switch (type) {
            case 'PROFESSIONAL':
                return 'Profissionais'
            case 'EMPLOYEE':
                return 'Funcionários'
            case 'COMPANY':
                return 'Administradores'
            default:
                return 'Usuários'
        }
    }

    const getIcon = () => {
        switch (conversation.type) {
            case 'AI_CHAT':
                return <HiSparkles className="text-secondary" />
            case 'BROADCAST_ALL':
                return <MdGroup className="text-success" />
            case 'BROADCAST_TYPE':
                return <MdGroup className="text-info" />
            default:
                return <MdPerson className="text-secondary" />
        }
    }

    const formatDate = (dateStr?: string) => {
        if (!dateStr) return ''

        const date = new Date(dateStr)
        const now = new Date()
        const diffDays = Math.floor(
            (now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24)
        )

        if (diffDays === 0) {
            return date.toLocaleTimeString('pt-BR', {
                hour: '2-digit',
                minute: '2-digit',
            })
        } else if (diffDays === 1) {
            return 'Ontem'
        } else if (diffDays < 7) {
            return date.toLocaleDateString('pt-BR', { weekday: 'short' })
        } else {
            return date.toLocaleDateString('pt-BR', {
                day: '2-digit',
                month: '2-digit',
            })
        }
    }

    return (
        <button
            type="button"
            onClick={onClick}
            className={`w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-base-200 transition-colors border-b border-base-200 ${isActive ? 'bg-base-200' : ''
                }`}
        >
            {/* Avatar/Icon */}
            <div
                className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${conversation.type === 'AI_CHAT'
                    ? 'bg-secondary/20'
                    : conversation.type === 'BROADCAST_ALL'
                        ? 'bg-success/20'
                        : conversation.type === 'BROADCAST_TYPE'
                            ? 'bg-info/20'
                            : 'bg-secondary/20'
                    }`}
            >
                {getIcon()}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                    <span className="font-medium truncate">{getTitle()}</span>
                    <span className="text-xs text-base-content/50 flex-shrink-0">
                        {formatDate(conversation.lastMessageAt)}
                    </span>
                </div>
                <div className="flex items-center justify-between gap-2 mt-0.5">
                    <p className="text-sm text-base-content/70 truncate">
                        {conversation.lastMessagePreview || 'Sem mensagens'}
                    </p>
                    {conversation.unreadCount > 0 && (
                        <span className="badge badge-secondary badge-sm flex-shrink-0">
                            {conversation.unreadCount > 99
                                ? '99+'
                                : conversation.unreadCount}
                        </span>
                    )}
                </div>
            </div>
        </button>
    )
}
